import {
	MAX_OFFLINE_ATTEMPTS,
	OFFLINE_RETRY_DELAY,
} from '../../config/networkStatus';
import {
	STORAGE_KEY_OFFLINE_ATTEMPTS,
	STORAGE_KEY_OFFLINE_PAUSED_AT,
} from '../../config/storageKeys';

/**
 * Checks if the browser is currently online
 */
export function isOnline(): boolean {
	return navigator.onLine;
}

/**
 * Gets the current number of consecutive offline attempts
 */
export function getOfflineAttempts(): number {
	const attempts = window.localStorage.getItem(STORAGE_KEY_OFFLINE_ATTEMPTS);
	return attempts ? parseInt(attempts, 10) : 0;
}

/**
 * Increments the offline attempts counter
 */
export function incrementOfflineAttempts(): number {
	const current = getOfflineAttempts();
	const newCount = current + 1;
	window.localStorage.setItem(STORAGE_KEY_OFFLINE_ATTEMPTS, newCount.toString());
	return newCount;
}

/**
 * Resets the offline attempts counter (called when back online and successful)
 */
export function resetOfflineAttempts(): void {
	window.localStorage.removeItem(STORAGE_KEY_OFFLINE_ATTEMPTS);
	window.localStorage.removeItem(STORAGE_KEY_OFFLINE_PAUSED_AT);
}

/**
 * Marks the start of an offline pause period
 */
export function markOfflinePause(): void {
	window.localStorage.setItem(STORAGE_KEY_OFFLINE_PAUSED_AT, Date.now().toString());
}

/**
 * Gets the timestamp when offline pause started
 */
export function getOfflinePausedAt(): number | null {
	const pausedAt = window.localStorage.getItem(STORAGE_KEY_OFFLINE_PAUSED_AT);
	return pausedAt ? parseInt(pausedAt, 10) : null;
}

/**
 * Checks if we should retry after an offline pause
 * Returns true if enough time has passed since the pause started
 */
export function shouldRetryAfterOfflinePause(): boolean {
	const pausedAt = getOfflinePausedAt();
	if (!pausedAt) {
		return true; // No pause recorded, can proceed
	}

	const elapsed = Date.now() - pausedAt;
	return elapsed >= OFFLINE_RETRY_DELAY;
}

/**
 * Checks if we have exceeded the maximum offline attempts
 */
export function hasExceededOfflineAttempts(): boolean {
	return getOfflineAttempts() >= MAX_OFFLINE_ATTEMPTS;
}

/**
 * Determines if network requests should be paused
 * Returns true if we should pause requests (offline with max attempts exceeded and not enough time elapsed)
 */
export function shouldPauseNetworkRequests(): boolean {
	if (isOnline()) {
		return false;
	}

	if (!hasExceededOfflineAttempts()) {
		return false;
	}

	return !shouldRetryAfterOfflinePause();
}

/**
 * Registers event listeners for online/offline status changes
 * Returns a cleanup function to remove the listeners
 */
export function onNetworkStatusChange(callback: (online: boolean) => void): () => void {
	const handleOnline = () => callback(true);
	const handleOffline = () => callback(false);

	window.addEventListener('online', handleOnline);
	window.addEventListener('offline', handleOffline);

	return () => {
		window.removeEventListener('online', handleOnline);
		window.removeEventListener('offline', handleOffline);
	};
}
